import { Alert } from "@/components/Alert";
import { Card } from "@/components/Card";
import { H3, H4 } from "@/components/Typography";
import { getMcrSummaryTableStats } from "@/utils/emailDashboardMetricsHelper";
import { faLightbulb } from "@fortawesome/pro-duotone-svg-icons";
import { Trans, useTranslation } from "react-i18next";

interface IProps {
  data: IGetEmailDashboardRS;
}

interface ITableRow {
  label: string;
  preDataships: JSX.Element | string | number;
  dataships: JSX.Element | string | number;
  isGreenRow?: boolean;
}

const McrSummaryTable = ({ data }: IProps) => {
  const { t } = useTranslation();
  const {
    marketingConsentRate,
    preDatashipsMarketingConsentRate,
    verifiedContacts,
    subscribedContacts,
    preDatashipsSubscribedContacts,
    nonSubscribedContacts,
    preDatashipsNonSubscribedContacts,
  } = getMcrSummaryTableStats(data?.rawData, data?.preDatashipsMarketableRate);

  const renderTableRow = ({
    label,
    preDataships,
    dataships,
    isGreenRow = false,
  }: ITableRow) => (
    <tr
      className={`${isGreenRow ? "bg-green-light/30" : "even:bg-gray-light/50"} border-b border-gray-light hover:bg-primary-light/50`}>
      <th className="p-10 text-left font-medium">{label}</th>
      <td
        className={`p-10 text-center ${isGreenRow && "font-bold text-primary"}`}>
        {preDataships}
      </td>
      <td
        className={`p-10 text-center ${isGreenRow && "font-bold text-green"}`}>
        {dataships}
      </td>
    </tr>
  );

  const renderTable = () => (
    <table className="mb-20 w-full">
      <thead className="border-b border-gray-light">
        <tr>
          <th></th>
          <th className="rounded-tl bg-primary p-10 text-white">
            {t("email.dashboard.mcrSummaryTable.withoutDataships")}
          </th>
          <th className="rounded-tr bg-green p-10 text-white">
            {t("email.dashboard.mcrSummaryTable.withDataships")}
          </th>
        </tr>
      </thead>
      <tbody>
        {renderTableRow({
          label: t("email.dashboard.mcrSummaryTable.verifiedContacts"),
          preDataships: verifiedContacts,
          dataships: verifiedContacts,
        })}
        {renderTableRow({
          label: t("email.dashboard.mcrSummaryTable.subscribedContacts"),
          preDataships: preDatashipsSubscribedContacts,
          dataships: subscribedContacts,
        })}
        {renderTableRow({
          label: t("email.dashboard.mcrSummaryTable.nonSubscribedContacts"),
          preDataships: preDatashipsNonSubscribedContacts,
          dataships: nonSubscribedContacts,
        })}
        {renderTableRow({
          label: t("email.dashboard.mcrSummaryTable.mcr"),
          preDataships: <span>{preDatashipsMarketingConsentRate}%</span>,
          dataships: <span>{marketingConsentRate}%</span>,
          isGreenRow: true,
        })}
      </tbody>
    </table>
  );

  const renderKeyInsight = () => {
    const marketingConsentRateInteger = parseInt(marketingConsentRate);
    const preDatashipsMarketingConsentRateInteger = parseInt(
      preDatashipsMarketingConsentRate,
    );

    if (preDatashipsMarketingConsentRateInteger >= marketingConsentRateInteger)
      return null;

    const mcrUplift =
      marketingConsentRateInteger - preDatashipsMarketingConsentRateInteger;

    return (
      <Alert
        variant="info"
        icon={faLightbulb}
        titleSlot={<H4 className="font-semibold">Key Insight</H4>}>
        <Trans
          i18nKey="email.dashboard.mcrSummaryTable.keyInsight"
          values={{
            preDatashipsMarketingConsentRate,
            marketingConsentRate,
            mcrUplift,
          }}>
          Dataships has improved your Marketing Consent Rate from
          <span className="font-bold text-primary">
            {preDatashipsMarketingConsentRate}%
          </span>
          to
          <span className="font-bold text-green">{marketingConsentRate}%</span>,
          representing a
          <span className="font-bold text-green">
            {mcrUplift} percentage point increase.
          </span>
        </Trans>
      </Alert>
    );
  };

  return (
    <div>
      <H3 className="mb-5 font-bold">
        {t("email.dashboard.titles.mcrSummary")}
      </H3>
      <hr className="mb-20" />
      <Card shadow additionalClassName="p-20 mb-30 border border-gray-light">
        {renderTable()}
        {renderKeyInsight()}
      </Card>
    </div>
  );
};

export default McrSummaryTable;
